// =============================================================================
// SimSense Platform - Login Page
// =============================================================================

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import { useTauri } from "../hooks/useTauri";
import { parseApiError } from "../utils/parseApiError";
import { markDesktopAuthComplete } from "../utils/desktopAuth";

type DesktopOutcome = "idle" | "success" | "error";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const desktopToken = searchParams.get("desktop_token");
  const isTauri = useTauri();
  const { login, loginWithGoogle, error: authError } = useAuthStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [desktopOutcome, setDesktopOutcome] = useState<DesktopOutcome>("idle");

  useEffect(() => {
    document.title = "Login | SimSense";
    // OT-332: Show registration disabled message from OAuth redirect
    if (searchParams.get("error") === "registration_disabled") {
      setError("New account registration is currently disabled. If you already have an account, please log in.");
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login({ email, password });
      if (desktopToken) {
        const completed = await markDesktopAuthComplete(desktopToken);
        setDesktopOutcome(completed ? "success" : "error");
      } else {
        navigate("/");
      }
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (desktopToken) {
      sessionStorage.setItem("ss_desktop_token", desktopToken);
    }
    loginWithGoogle();
  };

  const errorMessages = parseApiError(error || authError);

  if (desktopOutcome !== "idle") {
    const isSuccess = desktopOutcome === "success";
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "24px",
          background: "radial-gradient(circle at top right, #1e293b, #0a0e1a)",
        }}
      >
        <div
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: "12px",
            padding: "40px",
            maxWidth: "440px",
            width: "100%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontSize: "24px",
              fontWeight: 800,
              color: isSuccess ? "#22c55e" : "#f87171",
              marginBottom: "12px",
            }}
          >
            {isSuccess ? "Login Successful" : "Desktop Sync Failed"}
          </h1>
          <p style={{ color: "var(--text2)", fontSize: "15px" }}>
            {isSuccess
              ? "You can close this tab and return to the SimSense desktop app."
              : "Web sign-in succeeded, but the desktop app did not receive the session. Return to the desktop app and try again."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        background: "radial-gradient(circle at top right, #1e293b, #0a0e1a)",
        padding: isTauri ? "60px 24px 24px" : "24px",
      }}
    >
      {isTauri && (
        <div
          data-tauri-drag-region=""
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "48px",
            zIndex: 50,
            // @ts-ignore
            WebkitAppRegion: "drag",
          }}
        />
      )}
      {!isTauri && (
        <Link
          to="/"
          style={{
            position: "absolute",
            top: "20px",
            left: "24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            textDecoration: "none",
            color: "var(--text2)",
            fontSize: "14px",
            fontWeight: 500,
            transition: "color 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text1)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text2)")}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 12L6 8L10 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Home
        </Link>
      )}
      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          padding: "40px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                color: "var(--text1)",
                marginBottom: "8px",
                letterSpacing: "-0.025em",
              }}
            >
              SimSense
            </h1>
          </Link>
          <p style={{ color: "var(--text2)", fontSize: "15px" }}>
            Sign in to your SimSense account
          </p>
        </div>

        {errorMessages.length > 0 && (
          <div
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              color: "#f87171",
              padding: "14px 16px",
              borderRadius: "8px",
              fontSize: "14px",
              marginBottom: "20px",
              lineHeight: 1.5,
            }}
          >
            {errorMessages.length === 1 ? (
              errorMessages[0]
            ) : (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {errorMessages.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label
              style={{
                display: "block",
                color: "var(--text2)",
                fontSize: "13px",
                marginBottom: "6px",
                fontWeight: 600,
              }}
            >
              Email Address
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                color: "var(--text1)",
                fontSize: "15px",
                outline: "none",
              }}
              placeholder="you@example.com"
            />
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
              <label style={{ color: "var(--text2)", fontSize: "13px", fontWeight: 600 }}>
                Password
              </label>
              <button
                type="button"
                onClick={() => alert("Password reset is coming soon. For now, please contact support at simsense.app")}
                style={{ color: "var(--accent)", fontSize: "12px", textDecoration: "none", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}
              >
                Forgot Password?
              </button>
            </div>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                color: "var(--text1)",
                fontSize: "15px",
                outline: "none",
              }}
              placeholder="password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              background: "var(--accent)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              marginTop: "8px",
              transition: "all 0.15s ease",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div style={{ display: "flex", alignItems: "center", margin: "24px 0", gap: "12px" }}>
          <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
          <span
            style={{
              color: "var(--text3)",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            OR
          </span>
          <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
        </div>

        <button
          onClick={handleGoogleLogin}
          style={{
            width: "100%",
            padding: "12px",
            background: "#fff",
            color: "#1f2937",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            transition: "all 0.15s ease",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path
              fill="#4285F4"
              d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.91c1.71-1.57 2.69-3.88 2.69-6.62z"
            />
            <path
              fill="#34A853"
              d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.33-1.58-5.04-3.7H1.02v2.33A8.997 8.997 0 0 0 9 18z"
            />
            <path
              fill="#FBBC05"
              d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H1.02A8.99 8.99 0 0 0 0 9c0 1.45.33 2.82.91 4.04l3.05-2.33z"
            />
            <path
              fill="#EA4335"
              d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A8.96 8.99 0 0 0 9 0 8.997 8.997 0 0 0 1.02 4.96L3.96 7.29C4.67 5.17 6.66 3.58 9 3.58z"
            />
          </svg>
          Continue with Google
        </button>

        <button
          onClick={() => {
            if (desktopToken) sessionStorage.setItem("ss_desktop_token", desktopToken);
            window.location.href = "/api/ss/auth/iracing";
          }}
          style={{
            width: "100%",
            padding: "12px",
            marginTop: "10px",
            background: "#0047AB",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            transition: "all 0.15s ease",
          }}
        >
          <img src="/logos/iracing-icon.png" alt="" width="22" height="22" style={{ borderRadius: "3px" }} />
          Continue with iRacing
        </button>

        <div style={{ marginTop: "32px", textAlign: "center" }}>
          <p style={{ color: "var(--text2)", fontSize: "14px" }}>
            Don't have an account?{" "}
            <Link to="/signup" style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>
              Create Account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
